/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { ReactNode } from 'react';
import { ReactComponent as ExitIcon } from '../assets/icons/exitIcon.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/loree-deleteIcon.svg';
import {
  getAdminDashboardFontFamilyList,
  getCustomHeaderFontDetails,
  getFeaturesList,
} from '../components/interactiveManagement/utils';
import { CONSTANTS } from '../components/editor/editorUtilityFunctions/constants';

const getInteractiveType = (privateState: boolean, globalState: boolean) => {
  switch (true) {
    case !privateState && globalState:
      return 'global';
    case !privateState && !globalState:
      return 'shared';
    default:
      return 'private';
  }
};

export const validateExternalLink = (url: string) => {
  try {
    const isValidUrl = new URL(url);
    return isValidUrl ? url : { error: 'null' };
  } catch (error) {
    return { error };
  }
};

export const getEditStatus = (
  privateState: boolean,
  globalState: boolean,
  isCollaborative: boolean,
  config: {
    myinteractiveedit: boolean;
    globalinteractiveedit: boolean;
    sharedinteractiveedit: boolean;
    collaborativeinteractiveedit: boolean;
  },
) => {
  const interactiveType = getInteractiveType(privateState, globalState);
  switch (true) {
    case isCollaborative:
      return config.collaborativeinteractiveedit;
    case interactiveType === 'private':
      return config.myinteractiveedit;
    case interactiveType === 'global':
      return config.globalinteractiveedit;
    case interactiveType === 'shared':
      return config.sharedinteractiveedit;
    default:
      return false;
  }
};

export const getModalDetails = (type: string, title?: string | JSX.Element) => {
  switch (type) {
    case 'Exit':
      return {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExitIcon />
            <span
              style={{ color: ' #585858', fontWeight: 400, fontSize: '16px', fontFamily: 'Lexend' }}
            >
              Exit Interactive
            </span>
          </div>
        ),
        body: (
          <div
            style={{
              color: '#585858',
              fontWeight: 350,
              fontSize: '14px',
              fontFamily: 'Lexend',
              paddingBottom: '16px',
            }}
            className='p-3'
          >
            Are you sure want to exit? Changes you made will not be saved.
          </div>
        ) as ReactNode,
      };
    case 'Delete':
      return {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DeleteIcon />
            <span
              style={{ color: ' #585858', fontWeight: 400, fontSize: '16px', fontFamily: 'Lexend' }}
            >
              Delete Interactive
            </span>
          </div>
        ),
        body: (
          <div
            style={{
              color: '#585858',
              fontWeight: 300,
              fontSize: '14px',
              fontFamily: 'Lexend',
              padding: '16px',
            }}
          >
            Are you sure you want to delete <b>{`"${title}"`}</b> permanently?
            <br />
            <br /> This action cannot be undone and the component will be deleted wherever it is
            embedded
          </div>
        ) as ReactNode,
      };
    case 'Remove':
      return {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DeleteIcon />
            <span
              style={{ color: ' #585858', fontWeight: 400, fontSize: '16px', fontFamily: 'Lexend' }}
            >
              Remove Section
            </span>
          </div>
        ),
        body: (
          <div style={{ marginBottom: '16px' }}>
            Are you want to remove <b>{`${title}`}</b>?
          </div>
        ) as ReactNode,
      };
    case 'Remove Option':
      return {
        title: 'Remove Option',
        body: (
          <div>
            Are you want to remove <b>{`${title}`}</b>?
          </div>
        ) as ReactNode,
      };
    default:
      return {
        title: '',
        body: (<></>) as ReactNode,
      };
  }
};

export const shuffleArrayValues = (arr: Array<string>) => {
  for (let i = arr.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    let temp = arr[i];
    arr[i] = arr[j];
    arr[j] = temp;
  }
  return arr;
};

export const attachCustomFontUrl = (ltiPlatformId?: string) => {
  const queryParams = new URLSearchParams(window.location.search);
  const paramObj: any = {};
  queryParams.forEach((value, key) => {
    paramObj[key] = value;
  });
  if (!ltiPlatformId && !paramObj.ltiPlatformId) return;
  const linkElement = document.createElement('link');
  linkElement.rel = 'stylesheet';
  linkElement.href = `${process.env.REACT_APP_CUSTOMFONT_API_URL}public/${
    ltiPlatformId ?? paramObj.ltiPlatformId
  }/fonts/loree_custom_font.css`;
  document.head.appendChild(linkElement);
};

const validHosts = [
  'https://master.d1uzdukkodhm61.amplifyapp.com/',
  'https://master.d273ugfkhsuatz.amplifyapp.com/',
  'https://develop-v3.crystaldelta.net/',
  'http://localhost:3000/',
  'https://loree-stg.crystaldelta.net/',
  'https://stg-loree-v2.crystaldelta.net/',
  'https://dev-loree-v2.crystaldelta.net/',
  'https://master.dsw63snfssb15.amplifyapp.com/',
  'https://new-develop.d19viipu2480ae.amplifyapp.com/',
  'https://main-v3.d273ugfkhsuatz.amplifyapp.com/',
  'https://main-v3.d1uzdukkodhm61.amplifyapp.com/',
];

export const isValidHost = (host: string) => {
  return validHosts.some((validHost) => host.startsWith(validHost));
};

export const getCustomConfigurationDetails = (customConfiguration: any, type: string) => {
  let config = customConfiguration.config ? JSON.parse(customConfiguration.config) : null;

  if (!config)
    return {
      config: null,
      customLinkStyleList: null,
      fontFamilyList: [],
      customHeaderStyleList: [],
    };
  // Get custom link style list if available
  const customLinkStyleList = config?.configsList?.customLinkStyle?.customLink;
  // Process config according to type
  config = getFeaturesList(config, type);

  const featureConfig = JSON.parse(customConfiguration.config ?? 'null');

  const fontFamilyList = getAdminDashboardFontFamilyList(featureConfig.configsList);
  const customHeaderStyleList = getCustomHeaderFontDetails(featureConfig.configsList);
  return {
    config,
    customLinkStyleList,
    fontFamilyList,
    customHeaderStyleList,
  };
};

export const appendCustomFontUrlToHeader = (fontFamilyConfigList: any) => {
  let customFontFaceList: string[] = [];
  let fontApiUrl = CONSTANTS.LOREE_GOOGLE_FONT_API_URL;
  for (const font of fontFamilyConfigList) {
    if (Object.hasOwn(font, 'url') && font.url && !Object.hasOwn(font, 'customFont')) {
      const api = font.url.split('=');
      fontApiUrl += `&family=${api[1]}`;
    }
    if (Object.hasOwn(font, 'customFont')) {
      customFontFaceList.push(font.customFont as string);
    }
  }

  const linkTag = document.createElement('link');
  linkTag.setAttribute('rel', 'stylesheet');
  linkTag.setAttribute('href', fontApiUrl);
  linkTag.setAttribute('id', 'customFontsLink');

  const headElement = document.head;

  const existingCustomFontsLink = headElement.getElementsByClassName('customFontsLink')[0];
  existingCustomFontsLink?.remove();
  const existingCustomFonts = headElement.getElementsByClassName('customFonts')[0];
  existingCustomFonts?.remove();

  headElement?.appendChild(linkTag);

  if (customFontFaceList.length > 0) {
    const styleTag = document.createElement('style');
    for (const customFontFace of customFontFaceList) {
      styleTag.append(customFontFace);
    }
    headElement?.appendChild(styleTag);
  }
};
